import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Laminates from './pages/Laminates';
import LaminateDetail from './pages/LaminateDetail';
import ContactUs from './pages/ContactUs';

// Actual laminate design names (same as in Laminates.js)
const LAMINATE_DESIGNS = [
  "9101 ZR", "9117 ZR", "9118 ZR", "9122 ZR", "9123 ZR", "9143 ZR", "9144 ZR", "9145 ZR",
  "9207 ZR", "9208 ZR", "9209 ZR", "9252 ZR", "9253 ZR", "9402 ZR", "9403 ZR", "9404 ZR",
  "9405 ZR", "9406 ZR", "9407 ZR", "9408 ZR", "9441 ZR", "9446 ZR", "9447 ZR", "9448 ZR",
  "9461 ZR", "9462 ZR", "9463 ZR",
  "9242 CO", "9402 CO", "9403 CO", "9404 CO", "9409 CO", "9446 CO", "9448 CO", "9461 CO",
  "9462 CO", "9463 CO",
  "9410 RX", "9411 RX", "9441 RX", "9443 RX",
  "9242 SF", "9402 SF", "9403 SF", "9405 SF", "9406 SF", "9407 SF", "9441 SF", "9443 SF",
  "9445 SF", "9446 SF", "9447 SF", "9448 SF", "9461 SF",
  "9101 UHG", "9117 UHG", "9118 UHG", "9122 UHG", "9123 UHG", "9143 UHG", "9144 UHG",
  "9145 UHG", "9207 UHG", "9208 UHG", "9209 UHG", "9242 UHG", "9252 UHG", "9253 UHG",
  "9402 UHG", "9403 UHG", "9405 UHG", "9407 UHG", "9444 UHG", "9446 UHG", "9448 UHG",
  "9461 UHG",
  "9242 HZ", "9406 HZ", "9407 HZ", "9448 HZ"
];

// Convert name to URL slug (e.g., "9101 ZR" -> "9101-zr")
const nameToSlug = (name) => name.toLowerCase().replace(/\s+/g, '-');

// Convert URL slug back to name (e.g., "9101-zr" -> "9101 ZR")
const slugToName = (slug) => slug.toUpperCase().replace(/-/g, ' ');

// Parse the current pathname
const parsePath = (pathname) => {
  const path = pathname === '/' ? '/home' : pathname;
  const segments = path.split('/').filter(Boolean);
  
  if (segments[0] === 'laminates' && segments[1]) {
    // Laminate detail page: /laminates/9101-zr
    const slug = segments[1];
    const name = slugToName(slug);
    const index = LAMINATE_DESIGNS.findIndex(d => d === name);
    if (index !== -1) {
      return {
        page: 'laminateDetail',
        laminate: {
          id: index + 1,
          name: name,
          type: name.split(' ')[1]?.toLowerCase() || ''
        }
      };
    }
    return { page: 'laminates', laminate: null };
  }
  
  return { page: segments[0] || 'home', laminate: null };
};

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedLaminate, setSelectedLaminate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Handle URL-based routing
  useEffect(() => {
    const handleRouteChange = () => {
      const { page, laminate } = parsePath(window.location.pathname);
      setCurrentPage(page);
      setSelectedLaminate(laminate);
    };

    // Set initial page from URL
    handleRouteChange();

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  const navigateTo = (page) => {
    const url = page === 'home' ? '/' : `/${page}`;
    window.history.pushState({}, '', url);
    setCurrentPage(page);
    setSelectedLaminate(null);
    setSearchQuery('');
  };

  const handleSelectLaminate = (item) => {
    setSelectedLaminate(item);
    const slug = nameToSlug(item.name);
    window.history.pushState({}, '', `/laminates/${slug}`);
    setCurrentPage('laminateDetail');
  };

  const handleBackToLaminates = () => {
    setSelectedLaminate(null);
    window.history.pushState({}, '', '/laminates');
    setCurrentPage('laminates');
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query && currentPage !== 'laminates') {
      window.history.pushState({}, '', '/laminates');
      setCurrentPage('laminates');
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <Home />;
      case 'about':
        return <About onContactClick={() => navigateTo('contact')} />;
      case 'laminates':
        return <Laminates onSelectItem={handleSelectLaminate} searchQuery={searchQuery} />;
      case 'laminateDetail':
        return <LaminateDetail item={selectedLaminate} onBack={handleBackToLaminates} />;
      case 'contact':
        return <ContactUs />;
      default:
        return <Home />;
    }
  };

  return (
    <div className="App">
      <Header 
        currentPage={currentPage} 
        setCurrentPage={navigateTo} 
        onSearch={handleSearch}
      />
      <main className="main-content">
        {renderPage()}
      </main>
      <Footer />
    </div>
  );
}

export default App;
